import { MAX_ATTACHMENT_FILE_SIZE_BYTES } from './attachmentValidation';

const BYTES_PER_KIBIBYTE = 1024;
const BYTES_PER_MEGABYTE = BYTES_PER_KIBIBYTE ** 2;
const MAX_BUFFERED_PREVIEW_MEGABYTES = 25;
const MAX_BUFFERED_PREVIEW_SIZE_BYTES =
  MAX_BUFFERED_PREVIEW_MEGABYTES * BYTES_PER_MEGABYTE;

type AttachmentPreviewCategory =
  | 'audio'
  | 'image'
  | 'pdf'
  | 'presentation'
  | 'spreadsheet'
  | 'text'
  | 'video'
  | 'word';

type AttachmentPreviewUnavailableReason = 'file-too-large' | 'unsupported-type';

interface IAttachmentPreviewCapability {
  category?: AttachmentPreviewCategory;
  isPreviewable: boolean;
  reason?: AttachmentPreviewUnavailableReason;
}

interface IAttachmentPreviewInput {
  contentType: string;
  fileName: string;
  sizeBytes: number;
}

const IMAGE_EXTENSIONS = new Set(['bmp', 'gif', 'jpeg', 'jpg', 'png', 'svg', 'webp']);
const VIDEO_EXTENSIONS = new Set(['m3u8', 'mp4', 'webm']);
const AUDIO_EXTENSIONS = new Set([
  'aac',
  'flac',
  'm4a',
  'mid',
  'midi',
  'mp3',
  'mpeg',
  'oga',
  'ogg',
  'opus',
  'wav',
  'weba',
]);
const WORD_EXTENSIONS = new Set(['doc', 'docx', 'odt', 'rtf']);
const SPREADSHEET_EXTENSIONS = new Set(['csv', 'numbers', 'ods', 'tsv', 'xls', 'xlsx']);
const PRESENTATION_EXTENSIONS = new Set(['key', 'odp', 'ppt', 'pptx']);
const TEXT_EXTENSIONS = new Set([
  'bash',
  'c',
  'cc',
  'cpp',
  'cs',
  'css',
  'diff',
  'go',
  'h',
  'hpp',
  'html',
  'htm',
  'ini',
  'java',
  'js',
  'json',
  'jsx',
  'kt',
  'log',
  'markdown',
  'md',
  'patch',
  'php',
  'py',
  'rb',
  'rs',
  'scss',
  'sh',
  'sql',
  'swift',
  'tex',
  'toml',
  'ts',
  'tsx',
  'txt',
  'xml',
  'yaml',
  'yml',
]);

const BUFFERED_PREVIEW_CATEGORIES = new Set<AttachmentPreviewCategory>([
  'presentation',
  'spreadsheet',
  'text',
  'word',
]);

function getFileExtension(fileName: string): string {
  const separatorIndex = fileName.lastIndexOf('.');
  return separatorIndex === -1 ? '' : fileName.slice(separatorIndex + 1).toLowerCase();
}

function getPreviewCategory(
  contentType: string,
  fileName: string,
): AttachmentPreviewCategory | undefined {
  const normalizedContentType = contentType.toLowerCase();
  const extension = getFileExtension(fileName);

  if (IMAGE_EXTENSIONS.has(extension) || normalizedContentType.startsWith('image/')) {
    return 'image';
  }
  if (VIDEO_EXTENSIONS.has(extension) || normalizedContentType.startsWith('video/')) {
    return 'video';
  }
  if (AUDIO_EXTENSIONS.has(extension) || normalizedContentType.startsWith('audio/')) {
    return 'audio';
  }
  if (extension === 'pdf' || normalizedContentType === 'application/pdf') {
    return 'pdf';
  }
  if (
    WORD_EXTENSIONS.has(extension) ||
    [
      'application/msword',
      'application/rtf',
      'application/vnd.oasis.opendocument.text',
      'text/rtf',
    ].includes(normalizedContentType)
  ) {
    return 'word';
  }
  if (
    SPREADSHEET_EXTENSIONS.has(extension) ||
    normalizedContentType === 'application/vnd.apple.numbers' ||
    normalizedContentType === 'application/vnd.ms-excel' ||
    normalizedContentType === 'application/vnd.oasis.opendocument.spreadsheet' ||
    normalizedContentType ===
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
  ) {
    return 'spreadsheet';
  }
  if (
    PRESENTATION_EXTENSIONS.has(extension) ||
    normalizedContentType === 'application/vnd.apple.keynote' ||
    normalizedContentType === 'application/vnd.ms-powerpoint' ||
    normalizedContentType === 'application/vnd.oasis.opendocument.presentation' ||
    normalizedContentType ===
      'application/vnd.openxmlformats-officedocument.presentationml.presentation'
  ) {
    return 'presentation';
  }
  if (extension === 'pages' || normalizedContentType === 'application/vnd.apple.pages') {
    return 'word';
  }
  if (
    TEXT_EXTENSIONS.has(extension) ||
    normalizedContentType.startsWith('text/') ||
    normalizedContentType === 'application/json'
  ) {
    return 'text';
  }

  return undefined;
}

function getAttachmentPreviewCapability({
  contentType,
  fileName,
  sizeBytes,
}: IAttachmentPreviewInput): IAttachmentPreviewCapability {
  const category = getPreviewCategory(contentType, fileName);

  if (!category) {
    return { isPreviewable: false, reason: 'unsupported-type' };
  }

  const sizeLimit = BUFFERED_PREVIEW_CATEGORIES.has(category)
    ? MAX_BUFFERED_PREVIEW_SIZE_BYTES
    : MAX_ATTACHMENT_FILE_SIZE_BYTES;

  if (sizeBytes > sizeLimit) {
    return { category, isPreviewable: false, reason: 'file-too-large' };
  }

  return { category, isPreviewable: true };
}

export { getAttachmentPreviewCapability, MAX_BUFFERED_PREVIEW_SIZE_BYTES };
